import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Stage } from "../lusca-search-api/generated-src";
import { getAppSetting } from "../config/AppSettings";
import { MatchSetInfo } from "../types/health";
import { MatchSetState, MatchKeyType } from "../types/matchSetState";
import { CanonicalBusinessEvent } from "../types";
import { extractCandidateMatchKeys } from "../helpers/matchKeyUtils";
import { MARKETPLACE_TO_REALM } from "../helpers/marketplaceUtils";

export interface MatchSetData {
  matchSets: MatchSetInfo[];
  allClosed: boolean;
  openCount: number;
  total: number;
  isLoading: boolean;
  isError: boolean;
}

const CBE_REALM_TO_MSA: Record<string, string> = {
  USAmazon: "na",
  EUAmazon: "eu",
  JPAmazon: "fe",
};

function resolveRealm(
  cbes: CanonicalBusinessEvent[],
  marketplaceId: string
): string | undefined {
  // Try CBE realm field first
  const found = cbes.find((cbe) => {
    const raw = (cbe as unknown as Record<string, unknown>).realm;
    return typeof raw === "string" && raw !== "NONE" && CBE_REALM_TO_MSA[raw];
  });
  if (found) {
    const realm = (found as unknown as Record<string, unknown>).realm as string;
    return CBE_REALM_TO_MSA[realm];
  }
  // Fall back to marketplace mapping
  if (marketplaceId && MARKETPLACE_TO_REALM[marketplaceId])
    return MARKETPLACE_TO_REALM[marketplaceId];
  return undefined;
}

export default function useFetchMatchSets(
  stage: Stage,
  cbes: CanonicalBusinessEvent[],
  marketplaceId: string,
  enabled: boolean
): MatchSetData {
  const candidates = enabled ? extractCandidateMatchKeys(cbes) : [];
  const matchKeys = candidates.map((c) => c.key);
  const keyTypeMap = new Map(candidates.map((c) => [c.key, c.type]));
  const realm = enabled ? resolveRealm(cbes, marketplaceId) : undefined;
  const shouldFetch = enabled && matchKeys.length > 0 && !!realm;

  const { data, isLoading, isError } = useQuery<MatchSetInfo[]>(
    ["matchSets", stage, matchKeys.sort(), realm],
    async () => {
      if (!matchKeys.length || !realm) return [];
      const apiBasePath = `https://${getAppSetting("apiUrl") as string}/jwt`;
      const { data: responseData } = await axios.post<{
        matchSets?: MatchSetInfo[];
      }>(`${apiBasePath}/match-sets-query`, {
        stage,
        realm,
        matchKeys,
      });
      const results: MatchSetInfo[] = responseData.matchSets || [];
      const ROYALTY_ACCOUNTS = new Set(["20119", "20120", "14822"]);
      const DEFERRAL_ACCOUNTS = new Set(["23150"]);

      // Tag each result with its match key type, using account number as the
      // most reliable signal (e.g. royalty deferrals should be treated as royalty).
      return results.map((ms) => {
        const matchKeyType = keyTypeMap.get(ms.matchKey);
        const acct = ms.accountNumber ?? "";
        const resolvedType = ROYALTY_ACCOUNTS.has(acct)
          ? MatchKeyType.ROYALTY
          : DEFERRAL_ACCOUNTS.has(acct)
          ? MatchKeyType.DEFERRAL
          : matchKeyType;
        return { ...ms, matchKeyType: resolvedType };
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: shouldFetch,
    }
  );

  const matchSets = data || [];
  const openCount = matchSets.filter(
    (ms) => ms.state !== MatchSetState.CLOSED
  ).length;

  return {
    matchSets,
    allClosed: matchSets.length > 0 && openCount === 0,
    openCount,
    total: matchSets.length,
    isLoading: shouldFetch && isLoading,
    isError,
  };
}
